/**
 * @fileoverview AR-924 — In-memory `TeacherDeliverablePort` (Jest / review without a backend): reads the
 * real task through `fetchDeliverable`, overlays ./mockStore and fails with the same error kinds as HTTP.
 */

import { fetchDeliverable } from '../../service/taskTrackerService'
import type { TaskTrackDeliverable, TaskTrackDeliverableFile, TaskTrackDelivery } from '../../types'
import {
  TeacherDeliverableError,
  type BulkUploadResult,
  type TeacherDeliverablePort,
  type UploadFilesArgs,
  type UploadFilesForStudentsArgs,
} from '../ports'
import { fileKindOf, rejectFile } from '../fileRules'
import { normalizeLinkUrl } from '../../deliverableDrawer/linkUtils'
import {
  MOCK_CURRENT_TEACHER,
  dropIfEmpty,
  emptyTeacherDelivery,
  getMockDelivery,
  nextMockId,
  seedMockDelivery,
  withDelivery,
} from './mockStore'

export { seedMockDelivery, resetMockDeliveries, MOCK_CURRENT_TEACHER } from './mockStore'

const MOCK_LATENCY_MS = 350
const UPLOAD_TICKS = 8
const UPLOAD_TICK_MS = 120
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

const wait = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

async function load(activityId: string, studentId: string): Promise<TaskTrackDeliverable> {
  const base = await fetchDeliverable(activityId, studentId)
  // A real student submission (from the API) is kept as-is and stays read-only.
  return withDelivery(base, getMockDelivery(activityId, studentId) ?? base.delivery)
}

/** Backend guard: a teacher never writes over a delivery the student submitted. */
async function editableBase(activityId: string, studentId: string): Promise<TaskTrackDeliverable> {
  const base = await fetchDeliverable(activityId, studentId)
  if (base.delivery && !base.delivery.uploadedBy) {
    throw new TeacherDeliverableError('student_submitted', ['Student has already submitted this deliverable'])
  }
  return base
}

function teacherDelivery(activityId: string, studentId: string): TaskTrackDelivery {
  const current = getMockDelivery(activityId, studentId)
  if (!current?.uploadedBy) throw new TeacherDeliverableError('not_found', ['Delivery not found'])
  return current
}

function toDeliverableFile(file: File): TaskTrackDeliverableFile {
  const kind = fileKindOf(file.type)
  return {
    id: nextMockId('mock-file'),
    name: file.name,
    fileType: kind,
    downloadUrl: '#',
    sizeInBytes: file.size,
    fileCover: kind === 'image' ? URL.createObjectURL(file) : null,
  }
}

async function uploadFiles({ activityId, studentId, files, onProgress, signal }: UploadFilesArgs) {
  const base = await editableBase(activityId, studentId)
  const rejected = files.flatMap(file => {
    const reason = rejectFile(file)
    return reason ? [`${file.name}: ${reason}`] : []
  })
  if (rejected.length) throw new TeacherDeliverableError('file_rejected', rejected)

  const total = files.reduce((sum, file) => sum + file.size, 0)
  for (let tick = 1; tick <= UPLOAD_TICKS; tick += 1) {
    await wait(UPLOAD_TICK_MS)
    if (signal?.aborted) throw new TeacherDeliverableError('cancelled')
    onProgress?.({ loaded: Math.round((total * tick) / UPLOAD_TICKS), total })
  }
  const current = getMockDelivery(activityId, studentId) ?? emptyTeacherDelivery()
  const next: TaskTrackDelivery = {
    ...current,
    files: [...current.files, ...files.map(toDeliverableFile)],
  }
  seedMockDelivery(activityId, studentId, next)
  return withDelivery(base, next)
}

/** Backend `files/bulk`: a student who already submitted is skipped and reported, the rest get the files. */
async function uploadFilesForStudents(args: UploadFilesForStudentsArgs): Promise<BulkUploadResult> {
  const uploadedStudentIds: string[] = []
  const skipped: Array<{ studentId: string; reason: string }> = []
  for (const studentId of args.studentIds) {
    try {
      await uploadFiles({ ...args, studentId })
      uploadedStudentIds.push(studentId)
    } catch (error) {
      if (!(error instanceof TeacherDeliverableError) || error.kind !== 'student_submitted') throw error
      skipped.push({ studentId, reason: 'already_submitted' })
    }
  }
  return { uploadedStudentIds, skipped }
}

export const mockTeacherDeliverableAdapter: TeacherDeliverablePort = {
  fetch: load,
  uploadFiles,
  uploadFilesForStudents,

  async removeFile(activityId, studentId, fileId) {
    await wait(MOCK_LATENCY_MS)
    const current = teacherDelivery(activityId, studentId)
    dropIfEmpty(activityId, studentId, { ...current, files: current.files.filter(file => file.id !== fileId) })
    return load(activityId, studentId)
  },

  async addLink(activityId, studentId, url) {
    await wait(MOCK_LATENCY_MS)
    const normalized = normalizeLinkUrl(url)
    if (!normalized) throw new TeacherDeliverableError('invalid_url', ['Url is invalid'])
    const base = await editableBase(activityId, studentId)
    const current = getMockDelivery(activityId, studentId) ?? emptyTeacherDelivery()
    const link = {
      id: nextMockId('mock-link'),
      url: normalized,
      createdAt: new Date().toISOString(),
      addedBy: MOCK_CURRENT_TEACHER,
    }
    const next = { ...current, links: [...(current.links ?? []), link] }
    seedMockDelivery(activityId, studentId, next)
    return withDelivery(base, next)
  },

  async removeLink(activityId, studentId, linkId) {
    await wait(MOCK_LATENCY_MS)
    const current = teacherDelivery(activityId, studentId)
    dropIfEmpty(activityId, studentId, { ...current, links: (current.links ?? []).filter(link => link.id !== linkId) })
    return load(activityId, studentId)
  },

  async updateWorkCompletedAt(activityId, studentId, workCompletedAt) {
    await wait(MOCK_LATENCY_MS)
    if (!ISO_DATE.test(workCompletedAt)) {
      throw new TeacherDeliverableError('invalid_date', ['Work completed date is invalid'])
    }
    seedMockDelivery(activityId, studentId, { ...teacherDelivery(activityId, studentId), workCompletedAt })
    return load(activityId, studentId)
  },
}
